import React, { useState } from "react";
import { toResult, type Result, type CheckStatus } from "@sequesign/verifier-browser";

import { bundleToLoadedReceipt } from "../load/bundle.js";
import { runDemoVerification } from "../verify/run-demo.js";
import { runSerialized } from "../verify/serialize.js";

// A distinct Overview LAYOUT for the machine-spend demo: a spend statement /
// ledger whose every line is one recorded resource_purchased receipt a visitor
// recomputes in-browser. Unlike the two-card HeroPanel (Torbridge/Veralta),
// each ROW is independently verifiable, and one row is honestly over the
// per-purchase cap (conformant:false). All copy + the row→bundle wiring come
// from the scenario, so the component is reskinnable.

export interface LedgerRow {
  // Key into the `bundles` map: the recorded package this line verifies.
  bundleId: string;
  resource: string;
  category: string;
  amount: string;
  // Static hint shown before verification: does this line conform to the cap?
  expectConformant: boolean;
  overCapNote?: string;
}

export interface StatementConfig {
  eyebrow: string;
  headline: string;
  sub: string;
  title: string;
  subtitle: string;
  mandate: {
    capLabel: string;
    cap: string;
    allowedLabel: string;
    allowed: string;
    budgetLabel: string;
    budget: string;
    // 0..1 — fraction of the task budget spent, for the bar.
    budgetFraction: number;
  };
  rows: LedgerRow[];
  verifyLabel: string;
  verifyingLabel: string;
  verifyAllLabel: string;
  footNote: string;
  browserNote: string;
}

export interface LedgerCta {
  label: string;
  sub: string;
  onClick: () => void;
}

const STATUS_MARK: Record<CheckStatus, string> = {
  pass: "✓",
  fail: "✕",
  partial: "◐",
  not_evaluated: "–"
};

export function SpendLedger({
  statement,
  bundles,
  ctas
}: {
  statement: StatementConfig;
  bundles: Record<string, Record<string, string>>;
  ctas: LedgerCta[];
}) {
  const [results, setResults] = useState<Record<string, Result>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // One verification at a time: runDemoVerification installs into a SHARED
  // in-memory virtual filesystem (install clears it first), so two concurrent
  // verifications would read each other's files. `busy` names the row whose
  // verification is in flight and disables every button meanwhile.
  const [busy, setBusy] = useState<string | null>(null);

  async function verifyOne(bundleId: string): Promise<void> {
    const bundle = bundles[bundleId];
    if (!bundle) {
      setErrors((e) => ({ ...e, [bundleId]: `No recorded bundle for ${bundleId}` }));
      return;
    }
    try {
      const loaded = bundleToLoadedReceipt(bundleId, bundle);
      const started = performance.now();
      // Serialized process-wide (shared virtual FS): never overlap another row.
      const { report } = await runSerialized(() => runDemoVerification(loaded));
      const totalMs = performance.now() - started;
      const result = toResult(report, { totalMs, verifierVersion: "demo", commit: "demo" });
      setResults((r) => ({ ...r, [bundleId]: result }));
      setErrors((e) => ({ ...e, [bundleId]: "" }));
    } catch (err) {
      setErrors((e) => ({ ...e, [bundleId]: err instanceof Error ? err.message : String(err) }));
    }
  }

  async function verify(bundleId: string): Promise<void> {
    if (busy) return;
    setBusy(bundleId);
    try {
      await verifyOne(bundleId);
    } finally {
      setBusy(null);
    }
  }

  async function verifyAll(): Promise<void> {
    if (busy) return;
    setBusy("__all__");
    try {
      // Sequential: each verifyOne is itself serialized on the shared FS.
      for (const row of statement.rows) {
        await verifyOne(row.bundleId);
      }
    } finally {
      setBusy(null);
    }
  }

  const anyBusy = busy !== null;

  return (
    <section className="ledger">
      <div className="ledger-head">
        <div className="ledger-eyebrow">{statement.eyebrow}</div>
        <h1 className="ledger-headline">{statement.headline}</h1>
        <p className="ledger-sub">{statement.sub}</p>
      </div>

      <div className="statement">
        <div className="stmt-head">
          <div>
            <div className="stmt-title">{statement.title}</div>
            <div className="stmt-subtitle">{statement.subtitle}</div>
          </div>
          <div className="stmt-mandate">
            <div className="mfield">
              <div className="mfield-k">{statement.mandate.capLabel}</div>
              <div className="mfield-v">{statement.mandate.cap}</div>
            </div>
            <div className="mfield">
              <div className="mfield-k">{statement.mandate.allowedLabel}</div>
              <div className="mfield-v">{statement.mandate.allowed}</div>
            </div>
            <div className="mfield">
              <div className="mfield-k">{statement.mandate.budgetLabel}</div>
              <div className="mfield-v">{statement.mandate.budget}</div>
              <div className="budget-bar">
                <div
                  className="budget-fill"
                  style={{
                    width: `${Math.max(0, Math.min(1, statement.mandate.budgetFraction)) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="stmt-rows">
          {statement.rows.map((row) => (
            <LedgerLine
              key={row.bundleId}
              row={row}
              result={results[row.bundleId] ?? null}
              error={errors[row.bundleId] || null}
              busy={busy === row.bundleId}
              disabled={anyBusy}
              verifyLabel={statement.verifyLabel}
              verifyingLabel={statement.verifyingLabel}
              onVerify={() => verify(row.bundleId)}
            />
          ))}
        </div>

        <div className="stmt-foot">
          <span className="stmt-foot-note">{statement.footNote}</span>
          <button className="verify-all-btn" onClick={verifyAll} disabled={anyBusy}>
            {busy === "__all__" ? statement.verifyingLabel : statement.verifyAllLabel}
          </button>
        </div>
      </div>

      <p className="ledger-browser-note">{statement.browserNote}</p>

      <div className="ledger-ctas">
        {ctas.map((cta, i) => (
          <button className="ledger-cta" key={i} onClick={cta.onClick}>
            <span className="ledger-cta-label">{cta.label}</span>
            <span className="ledger-cta-sub">{cta.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function LedgerLine({
  row,
  result,
  error,
  busy,
  disabled,
  verifyLabel,
  verifyingLabel,
  onVerify
}: {
  row: LedgerRow;
  result: Result | null;
  error: string | null;
  busy: boolean;
  disabled: boolean;
  verifyLabel: string;
  verifyingLabel: string;
  onVerify: () => void;
}) {
  const over = !row.expectConformant;
  const verified = result !== null;
  return (
    <div className={"stmt-row" + (over ? " stmt-row-over" : "")}>
      <div className="stmt-row-main">
        <div className="stmt-resource">
          <div className="stmt-resource-name">{row.resource}</div>
          <div className="stmt-resource-cat">{row.category}</div>
        </div>
        <div className="stmt-amount">{row.amount}</div>
        <div className="stmt-status">
          {over ? (
            <span className="chip chip-over">✕ {row.overCapNote ?? "over cap"}</span>
          ) : (
            <span className="chip chip-ok">✓ within cap</span>
          )}
        </div>
        <div className="stmt-verify">
          <button
            className={"row-verify-btn" + (over ? " row-verify-btn-over" : "")}
            onClick={onVerify}
            disabled={busy || disabled}
          >
            {busy ? verifyingLabel : verified ? "Verify again" : verifyLabel}
          </button>
        </div>
      </div>

      {error && <div className="stmt-row-error">Verification error: {error}</div>}
      {result && <LineVerdict result={result} />}
    </div>
  );
}

function LineVerdict({ result }: { result: Result }) {
  const valid = result.valid;
  const conformant = result.conformant;

  const headline = !valid
    ? "Verification failed"
    : conformant === false
      ? "Verified: over the per-purchase cap (conformant: false)"
      : "Receipt verified";

  const detail = !valid
    ? `Rejected: ${result.reason ?? "see the failed check below."}`
    : conformant === false
      ? "Integrity, signatures, and the bound mandate all check out, and the receipt honestly records that the purchase exceeded the per-purchase cap."
      : "Integrity, signatures, evidence schema, and the bound spend mandate all check out.";

  const verdictClass = !valid ? "invalid" : conformant === false ? "warn" : "valid";

  return (
    <div className={`line-verdict line-verdict-${verdictClass}`}>
      <div className="line-verdict-head">
        <span className="line-verdict-mark" aria-hidden="true">
          {valid ? "✓" : "✕"}
        </span>
        <div>
          <div className="line-verdict-headline">{headline}</div>
          <div className="line-verdict-detail">{detail}</div>
        </div>
      </div>

      {result.trustAnchorMode === "self" && (
        <p className="line-verdict-anchor">
          Integrity self-check: verified against the witness key carried in this recorded receipt,
          not an independent third-party anchor. The <strong>Run live</strong> tab anchors to the
          witness's published key for a genuine third-party check.
        </p>
      )}

      <details className="line-checks">
        <summary>Show the {result.checks.length} checks the verifier ran</summary>
        <ul className="line-check-list">
          {result.checks.map((c) => (
            <li key={c.id} className={`line-check line-check-${c.status}`}>
              <span className="line-check-mark" aria-hidden="true">
                {STATUS_MARK[c.status]}
              </span>
              <span className="line-check-detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
